import type { UserAccessSummary } from '@zrsystem/contracts';

import type {
  NavigationLevel,
  NetworkSummary,
  PlaceSummary,
  TenantSummary,
} from '@/app/navigation-context/types.js';
import {
  NAVIGATION_LEVEL_TO_SCOPE,
  SCOPE_TO_NAVIGATION_LEVEL,
} from '@/app/navigation-context/types.js';
import { appPaths } from '@/app/router/paths.js';

function dedupeSummariesById<T extends { id: string }>(summaries: readonly T[]): T[] {
  const seen = new Set<string>();
  const unique: T[] = [];

  for (const summary of summaries) {
    if (seen.has(summary.id)) {
      continue;
    }

    seen.add(summary.id);
    unique.push(summary);
  }

  return unique;
}

export function buildPlaceSummaries(accesses: readonly UserAccessSummary[]): PlaceSummary[] {
  return dedupeSummariesById(
    accesses
      .filter(
        (access): access is UserAccessSummary & { placeId: string } =>
          access.scopeType === 'PLACE' && access.placeId != null,
      )
      .map((access) => ({
        id: access.placeId,
        name: access.label ?? 'Unidade',
        ...(access.subtitle !== undefined ? { subtitle: access.subtitle } : {}),
      })),
  );
}

export function buildTenantSummaries(accesses: readonly UserAccessSummary[]): TenantSummary[] {
  return dedupeSummariesById(
    accesses
      .filter(
        (access): access is UserAccessSummary & { tenantId: string } =>
          access.scopeType === 'TENANT' && access.tenantId != null,
      )
      .map((access) => ({
        id: access.tenantId,
        name: access.label ?? 'Organização',
        ...(access.subtitle !== undefined ? { subtitle: access.subtitle } : {}),
      })),
  );
}

export function buildNetworkSummaries(accesses: readonly UserAccessSummary[]): NetworkSummary[] {
  return dedupeSummariesById(
    accesses
      .filter(
        (access): access is UserAccessSummary & { organizationGroupId: string } =>
          access.scopeType === 'ORGANIZATION_GROUP' && access.organizationGroupId != null,
      )
      .map((access) => ({
        id: access.organizationGroupId,
        name: access.label ?? 'Rede',
        ...(access.subtitle !== undefined ? { subtitle: access.subtitle } : {}),
      })),
  );
}

export function hasPlatformAccess(accesses: readonly UserAccessSummary[]): boolean {
  return accesses.some((access) => access.scopeType === 'SYSTEM');
}

export function navigationLevelFromAccess(access: UserAccessSummary): NavigationLevel {
  return SCOPE_TO_NAVIGATION_LEVEL[access.scopeType];
}

export function activeEntityIdFromAccess(access: UserAccessSummary): string | null {
  switch (access.scopeType) {
    case 'PLACE':
      return access.placeId ?? null;
    case 'TENANT':
      return access.tenantId ?? null;
    case 'ORGANIZATION_GROUP':
      return access.organizationGroupId ?? null;
    case 'SYSTEM':
      return null;
    default: {
      const _exhaustive: never = access.scopeType;
      return _exhaustive;
    }
  }
}

export function buildContextLabel(
  level: NavigationLevel,
  activeEntityId: string | null,
  places: readonly PlaceSummary[],
  tenants: readonly TenantSummary[],
  networks: readonly NetworkSummary[],
): string {
  switch (level) {
    case 'place': {
      const place = places.find((item) => item.id === activeEntityId);
      return place?.name ?? 'Oficina';
    }
    case 'tenant': {
      const tenant = tenants.find((item) => item.id === activeEntityId);
      return tenant?.name ?? 'Organização';
    }
    case 'network': {
      const network = networks.find((item) => item.id === activeEntityId);
      return network?.name ?? 'Rede';
    }
    case 'platform':
      return 'Administração';
    default: {
      const _exhaustive: never = level;
      return _exhaustive;
    }
  }
}

export function contextSwitchSuccessMessage(destinationName: string): string {
  return `Alterado para ${destinationName} com sucesso`;
}

export function homePathForLevel(level: NavigationLevel, activeEntityId: string | null): string {
  switch (level) {
    case 'place':
      return appPaths.home;
    case 'tenant':
      return appPaths.org;
    case 'network':
      return activeEntityId ? appPaths.network(activeEntityId) : appPaths.home;
    case 'platform':
      return appPaths.adminOrganizations;
    default: {
      const _exhaustive: never = level;
      return _exhaustive;
    }
  }
}

/** True when the switcher/persisted unit is not the one stamped on the JWT. */
export function shouldRemintPlaceAccessToken(
  tokenPlaceId: string | null | undefined,
  selectedPlaceId: string | null,
): selectedPlaceId is string {
  return selectedPlaceId !== null && tokenPlaceId !== selectedPlaceId;
}

export function buildSwitchContextBody(level: NavigationLevel, entityId?: string) {
  const scopeType = NAVIGATION_LEVEL_TO_SCOPE[level];
  switch (level) {
    case 'place':
      return { scopeType, placeId: entityId };
    case 'tenant':
      return { scopeType, tenantId: entityId };
    case 'network':
      return { scopeType, organizationGroupId: entityId };
    case 'platform':
      return { scopeType };
    default: {
      const _exhaustive: never = level;
      return _exhaustive;
    }
  }
}

export function isStaticSwitcher(
  places: readonly PlaceSummary[],
  tenants: readonly TenantSummary[],
  networks: readonly NetworkSummary[],
  hasPlatform: boolean,
): boolean {
  const sectionCount =
    (places.length > 0 ? 1 : 0) +
    (tenants.length > 0 ? 1 : 0) +
    (networks.length > 0 ? 1 : 0) +
    (hasPlatform ? 1 : 0);

  if (sectionCount !== 1) {
    return false;
  }

  if (places.length === 1 && tenants.length === 0 && networks.length === 0 && !hasPlatform) {
    return true;
  }

  if (tenants.length === 1 && places.length === 0 && networks.length === 0 && !hasPlatform) {
    return true;
  }

  if (networks.length === 1 && places.length === 0 && tenants.length === 0 && !hasPlatform) {
    return true;
  }

  return hasPlatform && places.length === 0 && tenants.length === 0 && networks.length === 0;
}
